import { jsPDF } from "jspdf";

class PdfExport {
    constructor() {
        this.fontSize = 11;
        this.titleSize = 16;
        this.lineHeight = 8;
        this.indent = 10;
        this.startX = 20;
        this.startY = 20;
        this.pageWidth = 210;
        this.pageHeight = 297;
        this.marginRight = 20;
        this.tableStartX = 20;
        this.tableWidth = 170;
        this.textColor = '#2E2E2E';
        this.borderWidth = 1;
        this.footerHeight = 20;
    }

    downloadPDF(data, filename, placeholderText, projectName) {
        const doc = new jsPDF({
            orientation: 'p',
            unit: 'mm',
            format: 'a4',
            charSpace: 0,
            unicodeFont: true
        });
    
        const logoWidth = 50;
        const logoHeight = (82 * logoWidth) / 454;
        doc.addImage("/assets/img/" + projectName + "_logo_1.png", "PNG", this.startX, this.startY, logoWidth, logoHeight);
    
        this.y = this.startY + logoHeight + 10;
    
        doc.setFontSize(this.fontSize);
        doc.setTextColor(this.textColor);
    
        const lines = doc.splitTextToSize(placeholderText, this.tableWidth);
        lines.forEach(line => {
            doc.text(line, this.startX, this.y);
            this.y += this.lineHeight - 3;
        });
    
        this.y += this.lineHeight;
    
        const displayFilename = this.trimFilename(filename.replace(/\.[^/.]+$/, ""), 36);
        const footerFilename = this.trimFilename(filename.replace(/\.[^/.]+$/, ""), 15);

        this.addTableHeader(doc, "Metadata of ", displayFilename);
    
        const tableTopY = this.y;
    
        this.maxKeyWidth = this.calculateMaxKeyWidth(doc, data);
    
        this.addDataAsTable(doc, data, displayFilename);
    
        doc.rect(this.tableStartX, tableTopY, this.tableWidth, this.y - tableTopY - this.footerHeight);
    
        let currentPage = 1;
        while (currentPage <= doc.internal.getNumberOfPages()) {
            doc.setPage(currentPage);
            this.addFooter(doc, footerFilename, projectName);
            currentPage += 1;
        }
        doc.setPage(1);
    
        doc.save(filename + '-metadata.pdf');
    }

    trimFilename(filename, length) {
        filename = this.sanitizeValue(filename);
        if (filename.length <= length) return filename;
        return filename.substring(0, length - 3) + '...';
    }

    calculateMaxKeyWidth(doc, data, level = 0) {
        let maxWidth = 0;
        for (const [key, value] of Object.entries(data)) {
            const formattedKey = this.formatKey(key);
            const keyWidth = doc.getTextWidth(formattedKey) + level * this.indent;
            if (keyWidth > maxWidth) maxWidth = keyWidth;
            if (typeof value === 'object' && value !== null && !this.isSimpleObject(value)) {
                maxWidth = Math.max(maxWidth, this.calculateMaxKeyWidth(doc, value, level + 1));
            }
        }
        return maxWidth;
    }

    addTableHeader(doc, headerPrefix, displayFilename) {
        const headerHeight = 14;
        const horizontalPadding = 4;
        
        doc.setFillColor(0, 131, 143);
        doc.rect(this.tableStartX, this.y - headerHeight / 2, this.tableWidth, headerHeight, 'F');
        
        doc.setFontSize(this.titleSize);
        doc.setTextColor(255, 255, 255);
        
        const fullTitle = headerPrefix + displayFilename;
        
        doc.setFont(undefined, 'normal');
        doc.text(fullTitle, this.tableStartX + horizontalPadding, this.y - 1, { baseline: 'middle' });
        
        doc.setFont(undefined, 'normal');
        doc.setTextColor(this.textColor);
        doc.setFontSize(this.fontSize);
        
        this.y += this.lineHeight + 4;
        
        doc.setDrawColor(203, 203, 203);
        doc.line(this.tableStartX, this.y - 6, this.tableStartX + this.tableWidth, this.y - 6);
    }
    

    addDataAsTable(doc, data, displayFilename, level = 0) {
        if (level === 0) {
            Object.values(data).forEach(value => {
                if (typeof value === 'object' && value !== null) {
                    this.addDataAsTable(doc, value, displayFilename, level + 1);
                }
            });
        } else {
            for (const [key, value] of Object.entries(data)) {
                if (typeof value === 'object' && value !== null) {
                    if (this.isSimpleObject(value)) {
                        const formattedValue = this.formatSimpleObject('', value);
                        this.addTableRow(doc, this.formatKey(key), formattedValue, level, displayFilename);
                    } else {
                        this.addTableRow(doc, this.formatKey(key), '', level, displayFilename);
                        this.addDataAsTable(doc, value, displayFilename, level + 1);
                    }
                } else {
                    this.addTableRow(doc, this.formatKey(key), value, level, displayFilename);
                }
            }
        }
    }
    
    addTableRow(doc, key, value, level = 0, displayFilename) {
        this.checkAndAddPage(doc, displayFilename);
    
        const indentation = (level - 1) * this.indent;
        const keyX = this.tableStartX + 5 + indentation;
        const valueX = keyX + this.maxKeyWidth + 5;
        const valueMaxWidth = this.tableWidth - this.maxKeyWidth - indentation - 15;
    
        const keyLines = doc.splitTextToSize(key, this.maxKeyWidth - indentation);
        const valueLines = doc.splitTextToSize(String(value), valueMaxWidth);
        
        const linesCount = Math.max(keyLines.length, valueLines.length);
        const rowHeight = this.lineHeight * linesCount;
    
        if (this.y + rowHeight > this.pageHeight - this.footerHeight - 20) {
            doc.addPage();
            this.y = this.startY;
            this.addTableHeader(doc, "Metadata of ", displayFilename);
        }
    
        doc.setFillColor(255, 255, 255);
        doc.rect(this.tableStartX, this.y - 6, this.tableWidth, rowHeight, 'F');
    
        doc.setTextColor(this.textColor);
        for (let i = 0; i < linesCount; i++) {
            if (keyLines[i]) doc.text(keyLines[i], keyX, this.y);
            if (valueLines[i]) doc.text(valueLines[i], valueX, this.y);
            this.y += this.lineHeight;
        }
    
        doc.setDrawColor(203, 203, 203);
        doc.line(this.tableStartX, this.y - 6, this.tableStartX + this.tableWidth, this.y - 6);
    
        doc.line(this.tableStartX, this.y - 6, this.tableStartX, this.y - 6 - rowHeight);
        doc.line(this.tableStartX + this.tableWidth, this.y - 6, this.tableStartX + this.tableWidth, this.y - 6 - rowHeight);
    }      

    formatSimpleObject(key, obj) {
        if (obj.hasOwnProperty('value')) {
            const displayValue = this.sanitizeValue(obj.value);
            const displayKey = obj.key_formatted || key;
            return `${displayValue}`;
        } else {
            const values = Object.entries(obj)
                .filter(([k, _]) => k !== 'editable' && k !== 'key_formatted')
                .map(([k, v]) => `${k}: ${this.sanitizeValue(v)}`)
                .join(', ');
            return `${values}`;
        }
    }

    sanitizeValue(value) {
        if (typeof value !== 'string') return value;
    
        return value
            .normalize('NFC')
            .replace(/[^\x00-\x7F]/g, '')
            .trim();
    }

    isSimpleObject(obj) {
        const hasNestedObjects = Object.values(obj).some(v => 
            typeof v === 'object' && v !== null && !Array.isArray(v));
        return !hasNestedObjects;
    }

    formatKey(key) {
        const stringKey = String(key);
        if (!stringKey) return '';

        try {
            return stringKey
                .replace(/_/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        } catch (error) {
            console.error('Error formatting key:', error);
            return stringKey;
        }
    }

    checkAndAddPage(doc, displayFilename) {
        if (this.y > this.pageHeight - this.footerHeight - 20) {
            doc.addPage();
            this.y = this.startY;
            this.addTableHeader(doc, "Metadata of ", displayFilename);
        }
    }

    addFooter(doc, displayFilename, projectName) {
        const footerY = this.pageHeight - this.footerHeight;
        const site = 'www.' + projectName + '.com';

        doc.setFontSize(this.fontSize);
        doc.setTextColor(this.textColor);
        doc.text(`Metadata of`, this.startX, footerY, { baseline: 'middle' });
        // doc.setFont(undefined, 'bold');
        doc.text(displayFilename, this.startX + doc.getTextWidth('Metadata of '), footerY, { baseline: 'middle' });
        doc.setFont(undefined, 'normal');

        const projectNameWidth = doc.getTextWidth(site);
        const middleX = this.pageWidth / 2 - projectNameWidth / 2;
        doc.text(site, middleX, footerY, { baseline: 'middle' });

        const pageCount = doc.internal.getNumberOfPages();
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        const pageText = `Page ${currentPage}/${pageCount}`;
        const pageTextWidth = doc.getTextWidth(pageText);
        const rightX = this.pageWidth - this.marginRight - pageTextWidth;
        doc.text(pageText, rightX, footerY, { baseline: 'middle' });
    }
}

export default new PdfExport();